/* Copyright (c) 2020-present CloudIO Inc */

import {
  action,
  atom,
  emptyFunction,
  useStateXAtom,
  useStateXRef,
} from '@cloudio-saas/statex';
import { Box, Typography } from '@mui/material';
import {
  Button,
  Dialog,
  DialogContentText,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Icon from 'icons/Icon';
import { useOrgNameAndLogo } from 'layout/Header/OrgLogo';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo } from 'react';

export interface SessionTimeoutDialogProps {
  onOk: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const sessionTimeoutDialogRefPath = [
  'transient',
  'sessionTimeoutDialog',
  'ref',
];

const sessionTimeoutDialogOpenAtom = atom({
  path: ['transient', 'sessionTimeoutDialog', 'open'],
  defaultValue: false,
});

export const sessionTimeoutDialogAction = action<SessionTimeoutDialogProps>(
  ({ getRef, set }, props) => {
    const ref = getRef(sessionTimeoutDialogRefPath);
    if (ref) {
      ref.current = props;
      set(sessionTimeoutDialogOpenAtom, true);
    }
  },
);

function SessionTimeoutDialog() {
  const [open, setOpen] = useStateXAtom(sessionTimeoutDialogOpenAtom);
  const platform = usePlatform();
  const [{ publicMd }] = useOrgNameAndLogo();
  const { sessionLifetimeMinutes = 10080 } = publicMd;

  const ref = useStateXRef<SessionTimeoutDialogProps>(
    sessionTimeoutDialogRefPath,
    {
      onOk: emptyFunction,
    },
  );
  const { onOk } = ref.current;

  if (!open) {
    return null;
  }

  const handleOk = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    platform.forceUpdater(() => {
      platform.sessionTimeout = false;
      setOpen(false);
    });
    onOk?.(event);
  };

  return (
    <Dialog
      open={open}
      data-testid="io_sessiontimeout_dialog"
      sx={[{ bgcolor: 'transparent !important' }]}
    >
      <DialogContent sx={{ pb: '8px' }}>
        <DialogContentText
          sx={{
            p: '12px',
            pb: '8px',
            display: 'flex',
            gap: '20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Icon
              iconName="exclamation-triangle"
              height={32}
              width={32}
              color="warning"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '4px',
              pr: '20px',
            }}
          >
            <Typography fontWeight={700}>
              {`Your session is expired due to inactivity for ${sessionLifetimeMinutes} minutes.`}
            </Typography>
            <Typography>Please login again.</Typography>
          </Box>
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          p: '12px',
          pb: '16px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Button
          data-testid="confirm-sessiontimeout-yes"
          onClick={handleOk}
          variant="contained"
          size="small"
          fullWidth
          sx={{ m: '0px 150px 0px 180px' }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(SessionTimeoutDialog);
